/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { useFollowUpAnalytics } from '../../lib/queries/follow-ups.js';

const CARD =
  'rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900';

/** SLA resolution summary cards (CR-011) — resolved, on-time %, breached, avg time, open overdue. */
export function FollowUpSlaPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const { data } = useFollowUpAnalytics();
  if (data === undefined) {
    return null;
  }
  const onTimePct =
    data.resolved > 0 ? `${String(Math.round((data.onTime / data.resolved) * 100))}%` : '—';
  const cards: readonly {
    readonly key: string;
    readonly value: string;
    readonly danger?: boolean;
  }[] = [
    { key: 'resolved', value: String(data.resolved) },
    { key: 'onTimePct', value: onTimePct },
    { key: 'breached', value: String(data.breached), danger: data.breached > 0 },
    {
      key: 'avgHours',
      value: data.avgResolutionHours === null ? '—' : `${String(data.avgResolutionHours)}h`,
    },
    { key: 'openOverdue', value: String(data.openOverdue), danger: data.openOverdue > 0 },
  ];
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {cards.map((card) => (
        <div key={card.key} className={CARD}>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {t(`followUps.slaStats.${card.key}`)}
          </p>
          <p
            className={`mt-1 text-2xl font-semibold ${card.danger === true ? 'text-rose-600' : 'text-slate-800 dark:text-white'}`}
          >
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}
